import { useCallback, useContext, useEffect } from "react";
import FormElement from "rc-field-form";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/Button";
import Content from "./Content";
import { generateErrorsConfigForForm } from "../../../../../utils/helpers/errors";
import { useIsMasterCompany } from "../../../../../utils/hooks/company";
import { useModuleAccess } from "../../../../../utils/hooks/access";
import { useIsFieldDisabled } from "../../../helpers/useIsFieldDisabled";
import { OrgLevelGroupsContext } from "../context";

function Form({ mode, values, error, loading, submit, close }) {
	const { t } = useTranslation();
	const [form] = FormElement.useForm();
	const isMasterCompany = useIsMasterCompany();
	const { groups, setGroups } = useContext(OrgLevelGroupsContext);
	const { access } = useModuleAccess(
		"settings.general.organizationLevelGroup",
	);
	const disabled = useIsFieldDisabled({ ...access, mode });

	const onClose = useCallback(() => {
		form.resetFields();
		close();
	}, [form, close]);

	const onFinish = useCallback(
		(data) => {
			if (mode === "edit") {
				submit(data, (response) => {
					setGroups((prev) => ({
						result: prev.result.map((item) =>
							item.id === response.id ? response : item,
						),
						totalItems: prev.totalItems,
					}));
				});
			} else {
				submit(data, (response) => {
					setGroups((prev) => ({
						result: prev.result.concat(response),
						totalItems: prev.totalItems + 1,
					}));
				});
			}
		},
		[mode, setGroups, submit],
	);

	useEffect(() => {
		form.resetFields();
		form.setFieldsValue({
			status: "active",
			allocatedBy: values?.allocatedBy || false,
			hasBudget: values?.hasBudget || false,
			isRequired: values?.isRequired || false,
			hasHierarchy: values?.hasHierarchy || false,
			inclueLocationHierarchy: values?.inclueLocationHierarchy || false,
			useInClockIn: values?.useInClockIn || false,
			...values,
		});
	}, [form, values]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(
			[
				"code",
				"description",
				"status",
				"level",
				"allocatedBy",
				"hasBudget",
				"isRequired",
				"hasHierarchy",
				"inclueLocationHierarchy",
			],
			error,
		);
		form.setFields(fieldErrors);
	}, [error, form]);

	return (
		<FormElement
			form={form}
			className="d-flex flex-column justify-content-between h-100"
			onFinish={onFinish}
		>
			<div>
				<Content mode={mode} form={form} />
			</div>

			<div className="d-flex justify-content-end">
				<Button
					onClick={onClose}
					disabled={loading}
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
				>
					{t("cancel")}
				</Button>

				{(!disabled && isMasterCompany) && (
					<Button
						type="submit"
						className="btn-dark btn-sm shadow-none"
						loading={loading}
					>
						{t("save")}
					</Button>
				)}
			</div>
		</FormElement>
	);
}

export default Form;
